/**
 * useSchoolManifest — swaps the document's <link rel="manifest"> to point at
 * the school-specific dynamic manifest endpoint, AND sets <meta name="theme-color">
 * to the school's accent so the mobile browser chrome blends in.
 *
 * Reverts to the platform defaults on unmount so navigating away (e.g. to
 * /dashboard) restores the SchoolBoss PWA identity.
 */
import { useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLATFORM_MANIFEST = "/manifest.json";
const PLATFORM_THEME = "#1D4ED8";

function setLink(rel, href, attrs = {}) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function setMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
  return el;
}

export default function useSchoolManifest(slug, themeColor) {
  useEffect(() => {
    if (!slug) return undefined;

    const manifestUrl = `${BACKEND_URL}/api/public/schools/${slug}/manifest.webmanifest`;
    const iconUrl = `${BACKEND_URL}/api/public/schools/${slug}/icon.png`;

    const prevManifest = document.head.querySelector('link[rel="manifest"]')?.href;
    const prevTheme = document.head.querySelector('meta[name="theme-color"]')?.content;
    const prevAppleIcon = document.head.querySelector('link[rel="apple-touch-icon"]')?.href;
    const prevAppleTitle = document.head.querySelector('meta[name="apple-mobile-web-app-title"]')?.content;

    setLink("manifest", manifestUrl);
    if (themeColor) setMeta("theme-color", themeColor);
    setLink("apple-touch-icon", iconUrl);
    if (slug) setMeta("apple-mobile-web-app-title", slug);

    return () => {
      // Restore the platform identity when navigating away.
      setLink("manifest", prevManifest || PLATFORM_MANIFEST);
      setMeta("theme-color", prevTheme || PLATFORM_THEME);
      if (prevAppleIcon) setLink("apple-touch-icon", prevAppleIcon);
      if (prevAppleTitle) setMeta("apple-mobile-web-app-title", prevAppleTitle);
    };
  }, [slug, themeColor]);
}
